import type { ModuleId } from '@/modules/catalog';
import type { ViewAccess } from '@/shell/navigation';

/** Where to send someone, and what to call the way there. */
export interface NextAction {
  readonly label: string;
  readonly to: string;
}

interface Candidate extends NextAction {
  readonly permission: string;
  readonly module: ModuleId;
}

const BOOK_A_JOB: Candidate = {
  label: 'Book a job',
  to: '/jobs/new',
  permission: 'JOB_WRITE',
  module: 'CORE',
};

// In the order of how likely it is to be the next thing: booking is the desk's work, the pipeline
// is selling's, and the rest are for the people who hold that area and nothing nearer.
const CANDIDATES: readonly Candidate[] = [
  BOOK_A_JOB,
  { label: 'Open your deals', to: '/pipeline', permission: 'OPPORTUNITY_READ', module: 'CORE' },
  { label: 'Open invoices', to: '/invoices', permission: 'INVOICE_READ', module: 'BILLING' },
  { label: 'Open reports', to: '/reports', permission: 'SALES_REPORTING_READ', module: 'CORE' },
  { label: 'Open Field', to: '/field', permission: 'JOB_FIELD_EXECUTE', module: 'FIELD' },
  { label: 'Open customers', to: '/customers', permission: 'CUSTOMER_READ', module: 'CORE' },
];

function isOpenTo(candidate: Candidate, access: ViewAccess): boolean {
  return access.can(candidate.permission) && access.enabled(candidate.module);
}

function actionOf({ label, to }: Candidate): NextAction {
  return { label, to };
}

/**
 * The likeliest next step for someone whose list is empty.
 *
 * A desk role books a job, a seller opens their deals, accounting opens invoices. Only a step the
 * person may take and the release ships is offered, so the empty state never points at a wall.
 *
 * @param access what the person may open
 * @returns the step, or undefined when nothing suits them
 */
export function nextActionFor(access: ViewAccess): NextAction | undefined {
  const found = CANDIDATES.find((candidate) => isOpenTo(candidate, access));
  return found === undefined ? undefined : actionOf(found);
}

/**
 * The way to book a job, for the people who may.
 *
 * Today keeps it in reach whatever else is waiting, as the old overview's "New call" did.
 *
 * @param access what the person may open
 * @returns the step, or undefined when booking is not theirs to do
 */
export function bookJobFor(access: ViewAccess): NextAction | undefined {
  return isOpenTo(BOOK_A_JOB, access) ? actionOf(BOOK_A_JOB) : undefined;
}
